import { useQuery } from '@apollo/client'

import { CLICK_CODE, extractByTypename } from '@local/utils'

import {
  RecentOrderBookingSection_PrevBookingFragment,
  RecentOrderBookingSection_UserFragment,
} from '@web/federation/gql/graphql'
import { useErrorToast } from '@web/hooks/useErrorToast'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import { NavigationContext, useNavigation } from '@web/hooks/useNavigation'
import links from '@web/libs/constants/links'
import { RecentOrderBookingSectionQuery } from '@web/pages/v5/home/RecentOrderBookingSection/query'
import UIRecentOrderAndBookingSection from '@web/ui/pages/home/components/Sections/RecentOrderAndBookingSection'
import { UIRecentOrderAndBookingCardProps } from '@web/ui/pages/home/components/Sections/RecentOrderAndBookingSection/Card'
import { RecentBookingOrderTab } from '@web/ui/pages/home/components/Sections/RecentOrderAndBookingSection/constant'

// 스펙 wiki https://wiki.navercorp.com/pages/viewpage.action?pageId=2558051637
const convertUIRecentOrderAndBookingCardProps = ({
  isFromMapsApp,
  item,
  navigation,
  type,
}: {
  item: RecentOrderBookingSection_PrevBookingFragment
  type: RecentBookingOrderTab
  navigation: NavigationContext
  isFromMapsApp: boolean
}): UIRecentOrderAndBookingCardProps => {
  const isClosed = item.placeSummary?.notExist ?? false
  return {
    isAlive: item.isBizItemOpen,
    completeCount: item.bookingCount,
    type,
    address: item.placeSummary?.notExist ? undefined : item.placeSummary?.address?.formattedAddress ?? undefined,
    isClosed,
    imageUrl: item.businessImageUrl ?? undefined,
    placeName: item.placeSummary?.name ?? item.businessName,
    actionClickCode: type === '주문' ? CLICK_CODE['myfrequent.order'] : CLICK_CODE['myfrequent.book'],
    thumbnailClickCode: type === '주문' ? CLICK_CODE['myfrequent.ordercard'] : CLICK_CODE['myfrequent.bookingcard'],
    nClickBusinessId: item.businessId,
    nClickPlaceId: item.placeSummary?.id ?? undefined,
    // 섬네일 클릭시 콜백
    onClick: () => {
      // 장소 있는 경우 플레이스 상세로 이동 (앱/웹 분기는 goToPlaceDetail 에서)
      if (item.placeSummary?.id) {
        navigation.goToPlaceDetail(item.placeSummary.id)
        return
      }

      // 주문일 경우 주문홈으로 이동 https://m.booking.naver.com/order/bizes/{businessId}/items/{bizitemId}
      if (type === '주문') {
        navigation.push({
          url: links.ORDER_ITEM,
          params: { businessId: item.businessId, bizItemId: item.bizItemId },
          openWindow: true,
        })
        return
      }

      // 예약일 경우 예약홈으로 이동 https://m.booking.naver.com/booking/{유형}/bizes/{businessId} (유형 넣지 않아도 리다이렉션 됨)
      navigation.push({ url: links.BOOKING_BUSINESS_HOME, params: { businessId: item.businessId }, openWindow: true })
    },
    // 주문 / 예약하기 누를 경우 콜백
    onAction: item.isBizItemOpen
      ? () => {
          if (type === '주문') {
            // 주문일 경우 https://m.booking.naver.com/order/bizes/{businessId}/items/{bizitemId}?theme=place
            navigation.push({
              url: links.ORDER_ITEM,
              params: { businessId: item.businessId, bizItemId: item.bizItemId },
              extraQuery: { theme: 'place' },
              openWindow: true,
            })
            return
          }

          // 플레이스 있는 경우
          if (item.placeSummary?.id) {
            // 모웹: 플레이스>예약탭 https://m.place.naver.com/place/{placeId}/booking
            navigation.push({
              url: links.PLACE_DETAIL_BOOKING_TAB,
              params: { placeId: item.placeSummary!.id! },
              openWindow: true,
            })
            return
          }

          // 플레이스 없는 경우 예약홈 https://m.booking.naver.com/booking/{유형}/bizes/{businessId}
          navigation.push({
            url: links.BOOKING_BUSINESS_HOME,
            params: { businessId: item.businessId },
            openWindow: true,
          })
        }
      : undefined,
  }
}

type Props = {
  user: RecentOrderBookingSection_UserFragment | undefined | null
}

const RecentOrderBookingSection = ({ user }: Props) => {
  const { isFromMapsApp } = useGlobalStaticStateContext()
  const { data, error } = useQuery(RecentOrderBookingSectionQuery, { skip: !user })
  useErrorToast(error)

  const navigation = useNavigation()

  const { MeSucceed } = extractByTypename(data?.me)
  if (!MeSucceed) return null

  const { prevBookings, prevOrders, crmStamp } = MeSucceed

  const bookingCards =
    prevBookings?.map(item =>
      convertUIRecentOrderAndBookingCardProps({ item, type: '예약', navigation, isFromMapsApp }),
    ) ?? []
  const orderCards =
    prevOrders?.map(item =>
      convertUIRecentOrderAndBookingCardProps({ item, type: '주문', navigation, isFromMapsApp }),
    ) ?? []
  const stampCount = crmStamp?.totalCount ?? 0

  const handleClickStamp = () => {
    navigation.push({ url: crmStamp?.stampPageUrl.webUrl ?? links.BOOKING_MY_STAMPS, openWindow: true })
  }

  const handleClickNearByStore = () => {
    navigation.push({ url: links.PLACE_SMART_ORDER, openWindow: true })
  }

  return (
    <UIRecentOrderAndBookingSection
      orderCards={orderCards}
      bookingCards={bookingCards}
      stampCount={stampCount}
      onClickStamp={handleClickStamp}
      onClickNearByStore={handleClickNearByStore}
      bookingTabClickCode={CLICK_CODE['myfrequent.bookingtab']}
      orderTabClickCode={CLICK_CODE['myfrequent.ordertab']}
      findNearByClickCode={CLICK_CODE['myfrequent.ordernearby']}
      stampClickCode={CLICK_CODE['myfrequent.stamp']}
    />
  )
}

export default RecentOrderBookingSection
